import { CacheProvider, EmotionCache } from '@emotion/react'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { STRIPE_PUBLISHABLE_KEY } from 'constants/env'
import { UserProvider } from 'context'
import { NextPageWithLayout } from 'next'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { NextAdapter } from 'next-query-params'
import * as React from 'react'
import { ReactNode, useEffect } from 'react'
import { createTheme } from 'theme'
import { createEmotionCache } from 'theme/createEmotionCache'
import { QueryParamProvider } from 'use-query-params'
import { QueryParamAdapter } from 'use-query-params/src/types'
import { Roboto_Flex } from 'next/font/google'
import { ReactQueryProvider } from 'components/ReactQueryProvider/ReactQueryProvider'
import { Toaster } from 'react-hot-toast'

import 'simplebar-react/dist/simplebar.min.css'

function Adapter(props: {
  children: (adapter: QueryParamAdapter) => React.ReactElement | null
}) {
  return <NextAdapter {...props} />
}

const clientSideEmotionCache = createEmotionCache()
const theme = createTheme()

const robotoFlex = Roboto_Flex({
  subsets: ['latin'],
  weight: ['300', '400', '500', '600', '700', '800'],
  variable: '--font-roboto-flex',
  display: 'swap',
})

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)

interface MyAppProps extends AppProps {
  emotionCache?: EmotionCache
  Component: NextPageWithLayout
}

function MyApp({
  Component,
  pageProps,
  emotionCache = clientSideEmotionCache,
}: MyAppProps) {
  const getLayout = Component.getLayout || ((page: ReactNode) => page)

  useEffect(() => {
    // Workaround to fix FOUC(https://en.wikipedia.org/wiki/Flash_of_unstyled_content)
    document.documentElement.style.visibility = 'visible'
  }, [])

  return (
    <>
      <style jsx global>{`
        html {
          font-family: ${robotoFlex.style.fontFamily};
        }
      `}</style>
      <CacheProvider value={emotionCache}>
        <Head>
          <meta name='charset' content='utf-8' />
          <meta
            name='viewport'
            content='width=device-width, initial-scale=1, viewport-fit=cover, maximum-scale=1, user-scalable=0'
          />
          <title key='title'>Insight AI: Build AI Agents without code</title>
          <meta name='robots' content='noindex, nofollow'></meta>
        </Head>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <UserProvider>
            <QueryParamProvider
              adapter={Adapter}
              options={{ updateType: 'replaceIn' }}
            >
              <ReactQueryProvider>
                <Elements
                  stripe={stripePromise}
                  options={{
                    locale: 'en',
                  }}
                >
                  {getLayout(<Component {...pageProps} />)}
                </Elements>
              </ReactQueryProvider>
            </QueryParamProvider>
          </UserProvider>
          <Toaster position='bottom-center' />
        </ThemeProvider>
      </CacheProvider>
    </>
  )
}

export default MyApp
